import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject } from 'rxjs';
import {PazienteService} from "./paziente_service";
import {HttpResponse} from "@angular/common/http";
import {AuthService} from "./auth_service";
import { PrimeNGConfig } from 'primeng/api';
import {buildDayTableRenderRange} from "@fullcalendar/daygrid/internal";

@Injectable({
    providedIn: 'root'
})
export class LanguageService {
    private languageChanged = new BehaviorSubject<string>('it'); // Lingua predefinita
    languageChanged$ = this.languageChanged.asObservable();

    private localeSettings = new BehaviorSubject<any>({});
    localeSettings$ = this.localeSettings.asObservable();

    /**
     * Scatta solo quando il paziente cambia lingua dal selettore, dopo che la
     * nuova lingua e' in localStorage (da li' la legge l'interceptor per
     * l'Accept-Language) e sulla sua scheda. Le pagine che mostrano dati
     * tradotti dal backend lo ascoltano per richiederli nella lingua nuova.
     * Non scatta all'avvio, a differenza di languageChanged$.
     */
    private linguaCambiata = new Subject<string>();
    linguaCambiata$ = this.linguaCambiata.asObservable();

    /**
     * Pagine di sola visualizzazione: al cambio di lingua si ricaricano per
     * intero, perche' non c'e' niente di non salvato da perdere. Le pagine con
     * dati inseriti dal paziente (scheda del consulto, modifica consensi)
     * ascoltano invece linguaCambiata$ e ricaricano solo i testi tradotti.
     */
    private static readonly PAGINE_DA_RICARICARE = ['/paziente', '/consultipaziente', '/specialisticonsultati'];

    /**
     * Lingue offerte al paziente. I file in assets/i18n sono cinque (it, en, fr,
     * de, es), ma per ora si va online solo con italiano e inglese (12/10/2026):
     * fr, de ed es sono commentati anche nelle due tendine della topbar. Per
     * riattivarli basta rimetterli qui e togliere il commento alle <option>.
     */
    static readonly LINGUE = ['it', 'en'];

    /**
     * La lingua del browser, se e' fra quelle dell'interfaccia; altrimenti
     * l'inglese, che si capisce piu' facilmente dell'italiano.
     */
    static linguaDelBrowser(): string {
        const codice = (navigator.language || '').slice(0, 2).toLowerCase();
        return LanguageService.LINGUE.includes(codice) ? codice : 'en';
    }

    /**
     * La lingua con cui partire: quella scelta in precedenza, se e' ancora fra
     * quelle offerte, altrimenti quella del browser. Chi aveva scelto francese,
     * tedesco o spagnolo quando c'erano riparte cosi' nella lingua del browser
     * (in inglese, se il browser non e' ne' in italiano ne' in inglese), invece
     * di restare in una lingua che la tendina non mostra piu'.
     */
    static linguaIniziale(): string {
        const salvata = localStorage.getItem('lang');
        return salvata && LanguageService.LINGUE.includes(salvata) ? salvata : LanguageService.linguaDelBrowser();
    }

    constructor(
        private translate: TranslateService,
        private pazienteService: PazienteService,
        private authService: AuthService,
        private primengConfig: PrimeNGConfig
    ) {
        const lang = LanguageService.linguaIniziale();
        this.translate.setDefaultLang(lang);
        this.translate.use(lang);
        document.documentElement.lang = lang; // WCAG 3.1.1 (audit agosto 2026)
        this.traduciPrimeNG(lang);
    }

    changeLanguage(lang: string): void {
        const cambiata = !!this.translate.currentLang && lang != this.translate.currentLang;
        // Prima di tutto: l'interceptor legge da qui l'Accept-Language, e chi
        // reagisce al cambio deve gia' ricevere i dati nella lingua nuova.
        localStorage.setItem('lang', lang);
        this.applica(lang, () => {
            // La lingua si salva sulla scheda del paziente: gli altri ruoli non
            // hanno una scheda paziente, e la chiamata finirebbe per cercare un
            // paziente con l'id del medico.
            const fatto = () => { if (cambiata) { this.dopoIlCambio(lang); } };
            this.authService.getUserData('soft').subscribe({
                next: (response: HttpResponse<any>) => {
                    if (response.body?.ruolo != 'paziente') {
                        fatto();
                        return;
                    }
                    this.pazienteService.updateLingua(response.body.id, lang).subscribe({
                        next: (response: HttpResponse<any>) => { fatto(); },
                        error: (error) => { fatto(); }
                    })
                },
                error: (error) => { fatto(); }
            });
        });
    }

    /**
     * Dopo il cambio di lingua: le pagine di sola visualizzazione si ricaricano,
     * le altre ricevono linguaCambiata$. Si aspetta il salvataggio sulla scheda
     * del paziente perche' un ricaricamento lo interromperebbe.
     */
    private dopoIlCambio(lang: string): void {
        const percorso = window.location.pathname.replace(/\/+$/, '');
        if (LanguageService.PAGINE_DA_RICARICARE.includes(percorso)) {
            window.location.reload();
            return;
        }
        this.linguaCambiata.next(lang);
    }

    /**
     * L'area riservata di medico, amministratore e operatore esiste solo in
     * italiano: la scelta della lingua e' offerta al solo paziente. Chi non e'
     * paziente va quindi riportato all'italiano anche se il browser e' in
     * un'altra lingua, o se su quel browser era rimasta la scelta di un
     * paziente: senza selettore non avrebbe modo di tornare indietro.
     *
     * La preferenza in localStorage **non** si tocca: e' del paziente che usa
     * lo stesso browser, e deve ritrovarla al suo prossimo accesso.
     */
    allineaAlRuolo(ruolo: string): void {
        if (!ruolo || ruolo == 'paziente') {
            return;
        }
        if (this.translate.currentLang != 'it') {
            this.applica('it');
        }
    }

    /** Applica la lingua a interfaccia, calendario e attributo lang del documento. */
    private applica(lang: string, poi?: () => void): void {
        this.translate.use(lang).subscribe(() => {
            document.documentElement.lang = lang; // WCAG 3.1.1
            this.languageChanged.next(lang); // Notifica il cambio di lingua
            this.traduciPrimeNG(lang);
            if (poi) {
                poi();
            }
        });
    }

    /**
     * Le diciture che PrimeNG tiene per conto suo: nomi dei giorni e dei mesi del
     * calendario, etichette dei filtri delle tabelle, tasti del riquadro di
     * caricamento.
     *
     * Si leggono **dal file della lingua indicata** (`getTranslation(lang)`) e non
     * da `get()`, che lavora sulla lingua corrente: al primo caricamento la lingua
     * corrente e' ancora quella predefinita o il file non e' ancora in memoria, e
     * si ottenevano le diciture italiane. Era il motivo per cui, in una sessione
     * francese, il tasto «Aggiungi documenti» del riquadro di caricamento restava
     * in italiano mentre il tasto accanto — che e' nostro — era tradotto, e per cui
     * il calendario mostrava «Domenica» e «Gennaio» (01/10/2026).
     *
     * Le chiavi sono le parole italiane: quelle che nei file non ci sono tornano
     * identiche a se stesse, cosi' le etichette dei filtri restano in italiano
     * come erano, senza rompere niente.
     *
     * Una sola chiamata a `setTranslation` per calendario e diciture: con due,
     * l'ordine di arrivo decideva quale delle due vinceva.
     */
    private traduciPrimeNG(lang: string): void {
        this.translate.getTranslation(lang).subscribe((file: any) => {
            const d = (chiave: string) => (file && file[chiave]) || chiave;
            const calendario = (file && file['calendar']) || {};
            this.localeSettings.next(calendario);
            this.primengConfig.setTranslation({
                ...calendario,
                // etichette dei filtri delle tabelle
                'startsWith': d('Inizia con'),
                'contains': d('Contiene'),
                'notContains': d('Non contiene'),
                'endsWith': d('Finisce con'),
                'equals': d('Uguale a'),
                'notEquals': d('Diverso da'),
                'matchAll': d('Tutte le regole'),
                'matchAny': d('Almeno una regola'),
                'addRule': d('Aggiungi Regola'),
                'removeRule': d('Rimuovi Regola'),
                'clear': d('Pulisci'),
                'apply': d('Applica'),
                // riquadro di caricamento
                'choose': d('Aggiungi documenti'),
                'upload': d('Carica'),
                'cancel': d('Annulla'),
            });
        });
    }
}
